import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import type { ContactFormComponent } from "../../types";
import type { RendererConfig } from "../rendererConfig";
import { RuntimeLink } from "../../runtime/RendererRuntime";

/** Contact form renderer with name, email, subject and message fields. */
export function ContactFormRenderer({ component, config }: { component: ContactFormComponent; config?: RendererConfig }) {
  const subjectOptions = component.content.subjectOptions ?? [
    "Question générale",
    "Politique monétaire",
    "Supervision financière",
    "Billets et pièces",
    "Centrales des crédits",
    "Autre",
  ];
  const contactInfo = component.content.contactInfo ?? config?.contactInfo;
  return (
    <div className="grid gap-6 lg:grid-cols-[1.6fr,1fr]">
      <section className="p-0">
        <form method="post" className="space-y-5">
          <div>
            <label htmlFor="name" className="mb-1 block text-sm xl:text-base font-semibold text-[#00B862]">
              Nom et Prénom <span className="text-rose-600">*</span>
            </label>
            <input
              id="name"
              name="name"
              required
              placeholder="Ex. Marie Dupont"
              className="w-full rounded-sm border border-[#00B862] bg-white px-4 py-2 text-sm xl:text-base outline-none focus:border-[#00B862] focus:ring-2 focus:ring-[#00B862]/30"
            />
          </div>
          <div>
            <label htmlFor="email" className="mb-1 block text-sm xl:text-base font-semibold text-[#00B862]">
              Adresse e-mail <span className="text-rose-600">*</span>
            </label>
            <input
              id="email"
              type="email"
              name="email"
              required
              placeholder="nom@exemple.cd"
              className="w-full rounded-sm border border-[#00B862] bg-white px-4 py-2 text-sm xl:text-base outline-none focus:border-[#00B862] focus:ring-2 focus:ring-[#00B862]/30"
            />
          </div>
          <div>
            <label htmlFor="subject" className="mb-1 block text-sm xl:text-base font-semibold text-[#00B862]">
              Ma question concerne
            </label>
            <select
              id="subject"
              name="subject"
              defaultValue=""
              className="w-full appearance-none rounded-sm border border-[#00B862] bg-white px-4 py-2 text-sm xl:text-base outline-none focus:border-[#00B862] focus:ring-2 focus:ring-[#00B862]/30"
            >
              <option value="" disabled>
                Choisir une option
              </option>
              {subjectOptions.map((opt) => (
                <option key={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="message" className="mb-1 block text-sm xl:text-base font-semibold text-[#00B862]">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              rows={8}
              placeholder="Voici ma question"
              className="w-full rounded-sm border border-[#00B862] bg-white px-4 py-3 text-sm xl:text-base outline-none focus:border-[#00B862] focus:ring-2 focus:ring-[#00B862]/30"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs xl:text-sm text-slate-500">
              <span className="text-rose-600">*</span>Mentions obligatoires
            </span>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#00B862] px-4 py-2 text-sm xl:text-base font-semibold text-white hover:opacity-90"
            >
              J’envoie <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      </section>
      {contactInfo ? (
        <aside className="space-y-4">
          <div className="p-0">
            <h2 className="text-[15px] xl:text-[17px] font-semibold text-[#00B862]">Coordonnées</h2>
            <div className="mt-3 space-y-2 text-sm xl:text-base text-[#121212]">
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 text-[#00B862]" />
                <span>{contactInfo.address}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-[#00B862]" />
                <RuntimeLink href={`tel:${contactInfo.phone.replace(/\s+/g, "")}`} className="hover:underline">
                  {contactInfo.phone}
                </RuntimeLink>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-[#00B862]" />
                <RuntimeLink href={`mailto:${contactInfo.email}`} className="hover:underline">
                  {contactInfo.email}
                </RuntimeLink>
              </div>
            </div>
          </div>
        </aside>
      ) : null}
    </div>
  );
}


